/**
 * Enum-vaerdier. Skal matche CHECK-constraints i supabase/migrations/0001_schema.sql.
 * Vaerdierne er engelske - ogsaa `invoice_basis`, hvor scope-dokumentet skrev 'timer'.
 * Se docs/open-questions.md OQ-5.
 */

/**
 * The four roles the roles started with, i forrangsraekkefoelge: flest rettigheder foerst. Hvad
 * hver enkelt maa, staar i PERMISSIONS i auth/permissions.ts. Foer 08-10-2026 hed de controller,
 * project_manager og consultant - se `parseUserRole`.
 *
 * Since 09-10-2026 an admin can create, rename and delete roles on /admin/roller - these four
 * too. Every role is a row in `codepass_roles`; created ones rank after these - see
 * `RoleDefinition`.
 */
export const USER_ROLES = ['admin', 'finance', 'sales', 'developer'] as const;
export type BuiltInRole = (typeof USER_ROLES)[number];

/**
 * A role's key as `codepass_user_roles` and `codepass_roles` hold it: one of `USER_ROLES`, or a
 * role an admin created. Which keys exist is known at runtime only - see `RoleDefinition`.
 */
export type UserRole = string;

/**
 * Brugerstatus, som den staar i public.codepass_user_status.
 *
 * Stort begyndelsesbogstav bryder med de oevrige enums her, men vaerdierne er ikke vores:
 * de laeses og skrives raat mod en tabel Codepass deler med de andre OneCode-apps. At
 * normalisere dem her ville betyde en oversaettelse i begge retninger paa hver eneste
 * laesning. Brugeren ser 'Aktiv'/'Inaktiv' via USER_STATUS_LABELS.
 */
export const USER_STATUSES = ['Active', 'Inactive'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/**
 * Status paa en opgave i OnePro (public.todos.status).
 *
 * Samme forbehold som USER_STATUSES: vaerdierne er ikke vores. De laeses raat fra den
 * tabel Codepass deler med OnePro, med mellemrum og stort begyndelsesbogstav som OnePro
 * gemmer dem. Listen er de vaerdier der optraeder i dataene i dag og bruges til
 * filterdropdownen - status typet som `string` i svarene, saa en ny status i OnePro
 * vises raat frem for at forsvinde ud af listen. Brugeren ser dansk via
 * TIME_ENTRY_STATUS_LABELS.
 */
export const TIME_ENTRY_STATUSES = [
  'Not Started',
  'In Progress',
  'Waiting',
  'QA',
  'Testing',
  'Ready for invoicing',
  /* OnePro staver denne med store bogstaver, i modsaetning til 'Ready for invoicing'. */
  'Partially Ready for Invoicing',
  'Not Billable',
  'Internal',
  'Sales',
  /* Den, Codepass saetter - se INVOICED_STATUS. */
  'Invoiced',
] as const;
export type TimeEntryStatus = (typeof TIME_ENTRY_STATUSES)[number];

/**
 * Statussen Codepass saetter paa en opgave, naar BC har taget imod fakturaen, den er paa.
 *
 * **Codepass' eneste skrivning i OnePro's status.** Den afloeser `codepass_is_invoiced`
 * (beslutning 24-09-2026): i stedet for et flag, man kun ser i Codepass, staar det nu paa selve
 * opgaven, ogsaa i OnePro. Den tager opgaven ud af `INVOICEABLE_STATUSES`, og dermed ud af
 * fakturagrundlaget, uden en ekstra betingelse. Annulleres kladden, saettes statussen tilbage til
 * `READY_FOR_INVOICING` - se `releaseInvoiceHours`.
 *
 * **Skal vaere en vaerdi i OnePro's enum** - `todos.status` er `"StatusEnum"`, og en ukendt vaerdi
 * afvises med 22P02. 'Invoiced' findes i den (tjekket 24-09-2026). Brugeren ser 'Faktureret' via
 * TIME_ENTRY_STATUS_LABELS.
 */
export const INVOICED_STATUS: TimeEntryStatus = 'Invoiced';

/**
 * OnePro-statussen for en opgave, der er klar til at komme paa en faktura.
 *
 * Staar som en navngiven konstant og ikke som en streng spredt ud over flere filer: et halvt
 * dusin opslag afgraenser paa den, og en stavefejl ét af stederne ville give en tom liste frem
 * for en fejl - altsaa en faktura, der stille manglede timer.
 */
export const READY_FOR_INVOICING: TimeEntryStatus = 'Ready for invoicing';

/**
 * OnePro-statussen for en opgave, der er delvist klar til fakturering.
 *
 * **Stavet som OnePro gemmer den** - med store bogstaver, `Partially Ready for Invoicing` - og
 * altsaa ikke som `Ready for invoicing`. Efterproevet mod `todos` 23-09-2026 (26 raekker). En
 * sammenligning skrevet med de smaa bogstaver ville ramme ingenting.
 */
export const PARTIALLY_READY_FOR_INVOICING: TimeEntryStatus = 'Partially Ready for Invoicing';

/**
 * Statusserne, der goer en opgave valgbar paa /fakturering/ny.
 *
 * **Porten ind i fakturagrundlaget**, og hele porten: statussen siger "klar", og naar Codepass har
 * faktureret opgaven, staar den som `INVOICED_STATUS` og er ude. `todos.codepass_is_invoiced`
 * hverken laeses eller skrives. Se `pendingHours` i apps/api/src/demo/live-invoice-preview.ts.
 */
export const INVOICEABLE_STATUSES: readonly TimeEntryStatus[] = [
  READY_FOR_INVOICING,
  PARTIALLY_READY_FOR_INVOICING,
];

/**
 * OnePro-statusserne der aldrig bliver til penge paa en faktura.
 *
 * Arbejdet er udfoert paa aftalens tavle, men ingen kommer til at betale for det: `Not Billable`
 * er en beslutning om ikke at fakturere, `Internal` er vores eget arbejde, og `Sales` er tid
 * brugt paa at saelge. De tre er derfor det, der traekker den reelle timepris ned - se
 * `effectiveHourlyRate`.
 *
 * De oevrige statusser taeller som fakturerbare, ogsaa 'In Progress' og 'QA': arbejdet er ikke
 * faerdigt endnu, men det er heller ikke besluttet vaek. Ville man kun taelle 'Ready for
 * invoicing' med, ville enhver aftale midt i et sprint se ud til at tabe penge.
 */
export const NON_BILLABLE_STATUSES: readonly TimeEntryStatus[] = [
  'Not Billable',
  'Internal',
  'Sales',
];

/**
 * Statusserne i tabellen under Grundlag paa /fakturering/ny: arbejde paa fakturaens aftaler, som
 * ikke kommer med paa fakturaen.
 *
 * Ikke `NON_BILLABLE_STATUSES`: 'Internal' er vores eget arbejde og ikke noget, controlleren skal
 * tage stilling til ved faktureringen. De to her er beslutninger om ikke at opkraeve, truffet i
 * OnePro, og tabellen findes, saa de kan ses, foer fakturaen gemmes.
 */
export const UNBILLED_REVIEW_STATUSES: readonly TimeEntryStatus[] = ['Sales', 'Not Billable'];

/**
 * Kontraktfilteret paa /timer: vaerdien for "Andet".
 *
 * Dropdownen vaelger paa kontraktnummer (`ONC-1011`), og denne ene vaerdi staar for
 * restgruppen - opgaver paa en tavle uden aftale, og opgaver uden tavle. Ingen aftale kan
 * kollidere med den, fordi numrene fra Dynamics har formen `ONC-<nummer>`.
 *
 * Staar i shared og ikke som en streng i to filer: baade dropdownen og `GET /time-entries`
 * afgraenser paa den, og en stavefejl i én af dem ville give en tom liste frem for en fejl.
 */
export const CONTRACT_FILTER_OTHER = 'other';

/*
 * HOURS_VALIDATION_STATUSES og broen HOURS_VALIDATION_STATUS_AS_VALIDATION_STATUS er fjernet.
 *
 * De oversatte de danske vaerdier i `public.codepass_hours_status` til timernes engelske skala.
 * Codepass laeser ikke laengere den tabel: den er frosset ved et oejebliksbillede fra den
 * nedlagte Timekladde - 2.503 raekker mod 4.174 opgaver, 0 afviste, 0 begrundelser - og intet
 * skriver til den. Se demo/contract-hours.ts i API'et.
 *
 * VALIDATION_STATUSES nedenfor bliver staaende. Den hoerer til Codepass' egen `hours`-tabel og
 * har intet med den delte at gøre.
 */

/**
 * Regelkataloget i public.codepass_domain_rules.
 *
 * Vaerdierne er danske, fordi de ligger
 * danske i en tabel Codepass deler med de andre OneCode-apps, og de laeses og skrives raat.
 * En oversaettelse til engelske noegler skulle vedligeholdes i begge retninger paa hver
 * laesning og skrivning, og en fejl i én af dem ville vise en tom liste frem for en fejl.
 *
 * Ikke det samme som RULE_CATEGORIES, SEVERITIES og RUN_FREQUENCIES nedenfor: de hoerer til
 * Codepass' egen `rules`-tabel, som detekteringsmotoren laeser, og som ikke er rullet ud i
 * det projekt appen koerer mod. Se docs/implementation-status.md.
 */
export const DOMAIN_RULE_CATEGORIES = [
  'fakturering',
  'budget',
  'fornyelse',
  'timer',
  'compliance',
] as const;
export type DomainRuleCategory = (typeof DOMAIN_RULE_CATEGORIES)[number];

export const DOMAIN_RULE_SEVERITIES = ['info', 'advarsel', 'kritisk'] as const;
export type DomainRuleSeverity = (typeof DOMAIN_RULE_SEVERITIES)[number];

export const DOMAIN_RULE_FREQUENCIES = ['timelig', 'daglig', 'ugentlig'] as const;
export type DomainRuleFrequency = (typeof DOMAIN_RULE_FREQUENCIES)[number];

/**
 * Broerne mellem regelkatalogets danske vaerdier og alarmernes engelske.
 *
 * De to familier maa ikke slaas sammen: kategorien og alvorsgraden paa en *regel* laeses raat
 * fra public.codepass_domain_rules, som Codepass deler med de andre OneCode-apps, mens en
 * *alarm* er Codepass' egen og foelger husreglen om engelske enum-vaerdier. Oversaettelsen
 * sker derfor ét sted - her - og kun i én retning: fra reglen til alarmen den rejser.
 *
 * Uden dem ville en alarm baere 'fornyelse' i et felt UI slaar op i RULE_CATEGORY_LABELS, og
 * kategorien ville staa tom i filteret frem for at fejle synligt.
 */
export const DOMAIN_RULE_CATEGORY_AS_RULE_CATEGORY: Record<DomainRuleCategory, RuleCategory> = {
  fakturering: 'invoicing',
  budget: 'budget',
  fornyelse: 'renewal',
  timer: 'hours',
  compliance: 'compliance',
};

export const DOMAIN_RULE_SEVERITY_AS_SEVERITY: Record<DomainRuleSeverity, Severity> = {
  info: 'info',
  advarsel: 'warning',
  kritisk: 'critical',
};

/*
 * Der er ingen NOTIFICATION_CHANNELS laengere.
 *
 * Notifikationer gaar paa Teams, og kun der. Kanalvalget gav mening da der var to kanaler at
 * vaelge imellem; med én kanal ville et valg enten vaere kulisse eller en afmeldingsknap, og
 * en alarm om manglende fakturering er ikke noget den enkelte skal kunne slaa fra.
 *
 * public.codepass_user_notifications staar tilbage i det delte skema med sine gamle vaerdier.
 * Codepass hverken laeser eller skriver den - se docs/implementation-status.md.
 */

export const CUSTOMER_SEGMENTS = ['enterprise', 'mid_market', 'sme', 'public'] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];

/**
 * Kundens status paa /kunder. PRD §10.1 navngiver de tre: "Sund", "Betalingsadfaerd",
 * "Inaktiv". Vaerdierne her er engelske som de oevrige enums; brugeren ser dansk via
 * CUSTOMER_HEALTH_LABELS.
 *
 * Ikke en kolonne. Statusen udregnes pr. forespoergsel af `customerHealth` ud fra aktive
 * kontrakter og seneste faktura - se forbeholdet der om hvad 'payment_behaviour' faktisk
 * maaler i fase 1.
 */
export const CUSTOMER_HEALTHS = ['healthy', 'payment_behaviour', 'inactive'] as const;
export type CustomerHealth = (typeof CUSTOMER_HEALTHS)[number];

/**
 * The company check's overall verdict on a customer, shown as Status on /kunder. Ordered from
 * best to worst; 'unknown' means there was too little data to judge. See calc/company-check.ts.
 */
export const COMPANY_RATINGS = ['low', 'medium', 'high', 'critical', 'unknown'] as const;
export type CompanyRating = (typeof COMPANY_RATINGS)[number];

/** How one signal behind the rating came out. */
export const SIGNAL_LEVELS = ['good', 'ok', 'weak', 'bad'] as const;
export type SignalLevel = (typeof SIGNAL_LEVELS)[number];

/** Where a signal comes from: the CVR register, the published annual report, or our own BC ledger. */
export const SIGNAL_AREAS = ['registry', 'financials', 'payments'] as const;
export type SignalArea = (typeof SIGNAL_AREAS)[number];

/** What became of a posted invoice in the customer ledger. 'unresolved': closed, but not by a payment we can see. */
export const INVOICE_PAYMENT_OUTCOMES = [
  'paid',
  'credited',
  'open',
  'overdue',
  'unresolved',
] as const;
export type InvoiceOutcome = (typeof INVOICE_PAYMENT_OUTCOMES)[number];

/** The company's standing in the CVR register. */
export const COMPANY_REGISTRY_STATUSES = ['active', 'distress', 'bankrupt', 'ceased'] as const;
export type CompanyRegistryStatus = (typeof COMPANY_REGISTRY_STATUSES)[number];

export const CONTRACT_TYPES = ['fixed_price', 'time_materials', 'recurring', 'milestone'] as const;
export type ContractType = (typeof CONTRACT_TYPES)[number];

export const CONTRACT_STATUSES = [
  'draft',
  'awaiting_signature',
  'active',
  'completed',
  'cancelled',
  'expired',
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const BILLING_INTERVALS = ['monthly', 'quarterly', 'on_milestone', 'final_invoice'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const UNITS = ['hours', 'pcs', 'month', 'year', 'fixed'] as const;
export type Unit = (typeof UNITS)[number];

export const MILESTONE_STATUSES = [
  'planned',
  'in_progress',
  'reached',
  'invoiced',
  'cancelled',
] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

/**
 * Valideringsstatus i Codepass' egen `hours`-tabel.
 *
 * Tabellen er ikke rullet ud i det Supabase-projekt appen koerer mod, saa paa live-data staar
 * feltet altid paa `pending`: vurderingen af en time traeffes i OnePro, ikke i Codepass. Se
 * `ContractHour.validation_status` i API'et.
 */
export const VALIDATION_STATUSES = ['pending', 'approved', 'rejected', 'invoiced'] as const;
export type ValidationStatus = (typeof VALIDATION_STATUSES)[number];

export const INVOICE_STATUSES = [
  'draft',
  'approved',
  'sent_to_bc',
  'posted',
  'failed',
  'cancelled',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_BASES = [
  'milestone',
  'hours',
  'recurring',
  'fixed_price_partial',
  'final',
] as const;
export type InvoiceBasis = (typeof INVOICE_BASES)[number];

export const GENERATED_BY = ['agent', 'manual'] as const;
export type GeneratedBy = (typeof GENERATED_BY)[number];

export const SEVERITIES = ['info', 'warning', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const ALERT_STATUSES = ['open', 'in_progress', 'resolved', 'snoozed', 'dismissed'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

export const RULE_CATEGORIES = ['invoicing', 'budget', 'renewal', 'hours', 'compliance'] as const;
export type RuleCategory = (typeof RULE_CATEGORIES)[number];

export const RUN_FREQUENCIES = ['hourly', 'daily', 'weekly'] as const;
export type RunFrequency = (typeof RUN_FREQUENCIES)[number];

export const SYNC_SOURCES = ['getaccept', 'onepro', 'dynamics', 'bc', 'bank'] as const;
export type SyncSource = (typeof SYNC_SOURCES)[number];

export const SYNC_OPERATIONS = ['read', 'create', 'update', 'delete'] as const;
export type SyncOperation = (typeof SYNC_OPERATIONS)[number];

export const SYNC_STATUSES = ['success', 'failed', 'partial'] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

/** Statusser hvor en alarm stadig er aktiv og optaeller mod dedup-nooglen. */
export const ACTIVE_ALERT_STATUSES: readonly AlertStatus[] = ['open', 'in_progress', 'snoozed'];

/** Fakturastatusser der taeller som faktureret i progressions-beregninger. */
export const BILLED_INVOICE_STATUSES: readonly InvoiceStatus[] = ['sent_to_bc', 'posted'];
